import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Activity, Ban, Check, Copy, Download, KeyRound, Plug } from 'lucide-react'
import { ErrorApi } from '@/api/errores'
import { DialogoConMotivo } from '@/consola/DialogoConMotivo'
import {
  exportarActividadIa,
  useActividadIa,
  useClavesIa,
  useConexionIa,
  useCrearClaveIa,
  usePermisosAsignables,
  useRevocarClaveIa,
} from '@/dominio/ia/useIa'
import {
  NOMBRE_ESTADO_CLAVE,
  NOMBRE_MODO,
  NOMBRE_RESULTADO,
  NOMBRE_ROL_PLATAFORMA,
  type ClaveIa,
  type ClaveIaCreada,
  type EstadoClaveIa,
  type FiltroActividadIa,
  type ModoClaveIa,
  type ResultadoActividad,
  type RolPlataforma,
} from '@/dominio/ia/tipos'
import { formatearFecha, formatearFechaHora } from '@/lib/fecha'
import { copiarAlPortapapeles } from '@/lib/portapapeles'
import { Banner } from '@/ui/Banner'
import { Boton } from '@/ui/Boton'
import { CampoFormulario, CampoTexto, EntradaTexto, Selector } from '@/ui/CampoFormulario'
import { FilasEsqueleto } from '@/ui/Esqueleto'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { InsigniaEstado } from '@/ui/InsigniaEstado'
import { PanelLateral } from '@/ui/PanelLateral'
import type { Tono } from '@/ui/tonos'
import { EncabezadoPantalla, Tarjeta } from './-pantalla'

/**
 * Administración → Inteligencia artificial (`docs/adr/0026`).
 *
 * Las claves con que un asistente de IA entra a la plataforma y a la nómina de
 * este contribuyente, por MCP o por HTTP. La pantalla tiene que dejar claras
 * cuatro cosas, porque de ellas depende que una clave no haga más de lo debido:
 *
 *   **se ve una sola vez** — la plataforma guarda su huella, no la clave
 *
 *   **nunca tiene más que quien la crea** — y deja de servir si esa persona
 *   deja de ser administradora
 *
 *   **el modo es un segundo cerrojo** — solo lectura no escribe; con
 *   confirmación, lo irreversible exige confirmarlo en cada petición
 *
 *   **todo queda anotado como hecho por la IA** — la actividad de cada clave se
 *   ve aquí y se exporta
 *
 * No presenta a ningún proveedor de IA: qué IA se conecta lo decide el dueño.
 */
export const Route = createFileRoute('/inteligencia-artificial')({
  component: PantallaIa,
})

const NOMBRE_MAXIMO = 80

function PantallaIa() {
  const claves = useClavesIa()
  const [creando, setCreando] = useState(false)
  const [creada, setCreada] = useState<ClaveIaCreada | null>(null)
  const [revocando, setRevocando] = useState<ClaveIa | null>(null)
  const [actividad, setActividad] = useState<ClaveIa | null>(null)

  const sinPermiso = claves.error instanceof ErrorApi && claves.error.clase === 'permiso'
  const lista = claves.data?.claves ?? []
  const th = 'h-[var(--alto-fila)] px-2 text-etiqueta font-semibold text-gris-600'

  return (
    <div className="flex max-w-[1100px] flex-col gap-6">
      <EncabezadoPantalla
        titulo="Inteligencia artificial"
        descripcion="Claves para que un asistente de IA consulte y opere la plataforma y la nómina de este contribuyente, con los permisos que usted le dé. Qué asistente conectar lo decide usted."
        acciones={
          sinPermiso ? null : (
            <Boton variante="primario" icono={<KeyRound />} onClick={() => setCreando(true)}>
              Crear clave
            </Boton>
          )
        }
      />

      {sinPermiso ? (
        <Banner tono="ambar" titulo="Solo un administrador gestiona las claves de IA.">
          Pídale a un administrador del contribuyente que las cree o las revise. Una clave nunca tiene más permisos que quien la crea.
        </Banner>
      ) : null}

      {!sinPermiso ? (
        <Tarjeta titulo="Claves" relleno={false}>
          <div className="flex flex-col gap-2 border-b border-gris-200 px-4 py-3 text-cuerpo text-gris-700">
            <p>
              Cada clave entra con <strong className="font-semibold text-gris-900">los mismos permisos, el mismo aislamiento y la misma auditoría</strong>{' '}
              que una persona con ese rol. Lo que hace queda anotado como hecho por la IA, con el nombre de la clave.
            </p>
            <p className="text-etiqueta text-gris-600">
              Una por asistente: si uno se compromete, se revoca su clave sin tocar las demás. La revocación surte efecto en la petición siguiente.
            </p>
          </div>

          {claves.isError && !sinPermiso ? (
            <div className="p-4">
              <Banner tono="rojo" titulo="No se pudieron cargar las claves.">
                {claves.error instanceof Error ? claves.error.message : 'Vuelva a intentarlo.'}
              </Banner>
            </div>
          ) : !claves.isLoading && lista.length === 0 ? (
            <EstadoVacio variante="primera-vez" mensaje="Todavía no hay claves de IA. Cree una para conectar un asistente." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1040px] border-collapse text-cuerpo" data-densidad="normal">
                <thead className="bg-gris-50">
                  <tr className="border-b border-gris-300 text-left">
                    <th scope="col" className={th}>
                      Nombre
                    </th>
                    <th scope="col" className={`${th} w-[190px]`}>
                      Prefijo
                    </th>
                    <th scope="col" className={`${th} w-[150px]`}>
                      Modo
                    </th>
                    <th scope="col" className={`${th} w-[150px]`}>
                      Permisos
                    </th>
                    <th scope="col" className={`${th} w-[150px]`}>
                      Estado
                    </th>
                    <th scope="col" className={`${th} w-[140px] text-right`}>
                      Último uso
                    </th>
                    <th scope="col" className={`${th} w-[180px] text-right`}>
                      <span className="solo-lectores">Acciones</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {claves.isLoading ? (
                    <FilasEsqueleto filas={3} columnas={7} />
                  ) : (
                    lista.map((c) => {
                      const estado = estadoDe(c.estado)
                      return (
                        <tr key={c.id} className="h-[var(--alto-fila)] border-b border-gris-200 last:border-b-0">
                          <td className="max-w-0 truncate px-2 text-gris-900" title={c.nombre}>
                            {c.nombre}
                            <span className="block text-etiqueta text-gris-500">
                              {c.limitePorMinuto} por minuto{c.venceEl ? ` · vence el ${formatearFecha(c.venceEl)}` : ''}
                            </span>
                          </td>
                          <td className="mono px-2 text-gris-700">{c.prefijo}</td>
                          <td className="px-2 text-gris-700">{NOMBRE_MODO[c.modo].nombre}</td>
                          <td className="px-2 text-gris-700">
                            {c.rol ? `Rol ${NOMBRE_ROL_PLATAFORMA[c.rol].toLowerCase()}` : `${c.permisos.length} a medida`}
                          </td>
                          <td className="px-2">
                            <InsigniaEstado tono={estado.tono} aclaracion={c.revocadaEl ? formatearFecha(c.revocadaEl) : undefined}>
                              {estado.etiqueta}
                            </InsigniaEstado>
                          </td>
                          <td className="numero px-2 text-right text-gris-700">{c.ultimoUsoEl ? formatearFechaHora(c.ultimoUsoEl) : 'Nunca'}</td>
                          <td className="px-2">
                            <span className="flex justify-end gap-1">
                              <Boton tamano="menor" variante="discreto" icono={<Activity />} onClick={() => setActividad(c)}>
                                Actividad
                              </Boton>
                              {!c.revocadaEl ? (
                                <Boton tamano="menor" variante="discreto" icono={<Ban />} onClick={() => setRevocando(c)}>
                                  Revocar
                                </Boton>
                              ) : null}
                            </span>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Tarjeta>
      ) : null}

      {!sinPermiso ? <ComoConectar /> : null}

      {creando ? (
        <CrearClave
          onCerrar={() => setCreando(false)}
          onCreada={(clave) => {
            setCreando(false)
            setCreada(clave)
          }}
        />
      ) : null}

      {creada ? <ClaveRecienCreada clave={creada} onCerrar={() => setCreada(null)} /> : null}

      {revocando ? <Revocar clave={revocando} onCerrar={() => setRevocando(null)} /> : null}

      {actividad ? <Actividad clave={actividad} onCerrar={() => setActividad(null)} /> : null}
    </div>
  )
}

function estadoDe(estado: EstadoClaveIa): { tono: Tono; etiqueta: string } {
  switch (estado) {
    case 'vigente':
      return { tono: 'verde', etiqueta: NOMBRE_ESTADO_CLAVE.vigente }
    case 'revocada':
      return { tono: 'neutral', etiqueta: NOMBRE_ESTADO_CLAVE.revocada }
    default:
      return { tono: 'ambar', etiqueta: NOMBRE_ESTADO_CLAVE[estado] }
  }
}

function mensaje(error: unknown): string {
  return error instanceof Error ? error.message : 'Vuelva a intentarlo.'
}

function CrearClave({ onCerrar, onCreada }: { onCerrar: () => void; onCreada: (clave: ClaveIaCreada) => void }) {
  const asignables = usePermisosAsignables()
  const crear = useCrearClaveIa()
  const [nombre, setNombre] = useState('')
  const [modo, setModo] = useState<ModoClaveIa>('confirmar_irreversible')
  const [base, setBase] = useState<RolPlataforma | 'a_medida'>('consulta')
  const [permisos, setPermisos] = useState<string[]>([])
  const [vence, setVence] = useState('')
  const [limite, setLimite] = useState('60')

  const limpio = nombre.trim()
  const limiteNumero = Number.parseInt(limite, 10)
  const valido =
    limpio.length > 0 &&
    limpio.length <= NOMBRE_MAXIMO &&
    Number.isInteger(limiteNumero) &&
    limiteNumero >= 1 &&
    limiteNumero <= 600 &&
    (base !== 'a_medida' || permisos.length > 0)

  const delRol = base === 'a_medida' ? null : (asignables.data?.roles.find((r) => r.rol === base)?.permisos ?? [])

  const alternar = (p: string) => setPermisos((actuales) => (actuales.includes(p) ? actuales.filter((x) => x !== p) : [...actuales, p]))

  const enviar = () =>
    crear.mutate(
      {
        nombre: limpio,
        modo,
        rol: base === 'a_medida' ? null : base,
        permisos: base === 'a_medida' ? permisos : [],
        // El vencimiento es el final de ese día en República Dominicana (UTC−4, sin horario de verano).
        venceEl: vence ? `${vence}T23:59:59-04:00` : null,
        limitePorMinuto: limiteNumero,
      },
      { onSuccess: onCreada },
    )

  return (
    <PanelLateral
      abierto
      onCerrar={onCerrar}
      titulo="Crear una clave de IA"
      subtitulo="Para un asistente, con los permisos justos"
      ancho="normal"
      pie={
        <div className="flex justify-end gap-2">
          <Boton variante="discreto" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton variante="primario" icono={<KeyRound />} disabled={!valido || crear.isPending} onClick={enviar}>
            {crear.isPending ? 'Creando…' : 'Crear clave'}
          </Boton>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <CampoTexto
          etiqueta="Nombre"
          obligatorio
          maxLength={NOMBRE_MAXIMO}
          placeholder="Asistente de compras, Conciliación…"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          ayuda="Para qué es. Es lo que aparece como autor en la auditoría."
        />

        <fieldset className="flex flex-col gap-2">
          <legend className="pb-1 text-etiqueta font-medium text-gris-600">Modo</legend>
          {(Object.keys(NOMBRE_MODO) as ModoClaveIa[]).map((m) => (
            <label key={m} className="flex items-start gap-2 rounded-md border border-gris-300 p-2 text-cuerpo has-[:checked]:border-marca has-[:checked]:bg-marca-tenue">
              <input type="radio" name="modo" className="mt-1" checked={modo === m} onChange={() => setModo(m)} />
              <span>
                <span className="block font-medium text-gris-900">{NOMBRE_MODO[m].nombre}</span>
                <span className="text-etiqueta text-gris-600">{NOMBRE_MODO[m].descripcion}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <CampoFormulario etiqueta="Permisos" ayuda="Los de un rol, o una lista a medida. Nunca más de los que usted tiene.">
          {(atributos) => (
            <Selector {...atributos} value={base} onChange={(e) => setBase(e.target.value as RolPlataforma | 'a_medida')}>
              {(['consulta', 'contador', 'emisor', 'administrador'] as RolPlataforma[]).map((r) => (
                <option key={r} value={r}>
                  Los del rol {NOMBRE_ROL_PLATAFORMA[r].toLowerCase()}
                </option>
              ))}
              <option value="a_medida">A medida</option>
            </Selector>
          )}
        </CampoFormulario>

        {base === 'a_medida' ? (
          <fieldset className="flex flex-col gap-1">
            <legend className="pb-1 text-etiqueta font-medium text-gris-600">Qué puede hacer</legend>
            {(asignables.data?.permisos ?? []).map((p) => (
              <label key={p} className="flex items-center gap-2 text-etiqueta text-gris-700">
                <input type="checkbox" checked={permisos.includes(p)} onChange={() => alternar(p)} />
                <span className="mono">{p}</span>
              </label>
            ))}
          </fieldset>
        ) : delRol ? (
          <p className="text-etiqueta text-gris-600">
            <span className="font-medium text-gris-700">Incluye:</span> <span className="mono">{delRol.join(' · ')}</span>
          </p>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <CampoFormulario etiqueta="Vence" ayuda="Opcional. Sin fecha, hasta que se revoque.">
            {(atributos) => <EntradaTexto {...atributos} type="date" value={vence} onChange={(e) => setVence(e.target.value)} />}
          </CampoFormulario>
          <CampoFormulario etiqueta="Límite por minuto" ayuda="De 1 a 600 peticiones." obligatorio>
            {(atributos) => (
              <EntradaTexto {...atributos} numerico inputMode="numeric" value={limite} onChange={(e) => setLimite(e.target.value.replace(/\D/g, ''))} />
            )}
          </CampoFormulario>
        </div>

        {crear.isError ? (
          <Banner tono="rojo" titulo="No se pudo crear la clave.">
            {mensaje(crear.error)}
          </Banner>
        ) : null}
      </div>
    </PanelLateral>
  )
}

/**
 * La clave recién creada. Vive solo en el estado de este panel: al cerrarlo no
 * queda en ninguna parte, ni en la caché ni en el almacenamiento del navegador.
 */
function ClaveRecienCreada({ clave, onCerrar }: { clave: ClaveIaCreada; onCerrar: () => void }) {
  const [copiada, setCopiada] = useState(false)

  return (
    <PanelLateral
      abierto
      onCerrar={onCerrar}
      titulo="Clave creada"
      subtitulo={`${clave.nombre} · ${NOMBRE_MODO[clave.modo].nombre}`}
      pie={
        <Boton variante="primario" onClick={onCerrar}>
          Ya la copié
        </Boton>
      }
    >
      <div className="flex flex-col gap-4">
        <Banner tono="ambar" titulo="Cópiela ahora: no se vuelve a mostrar.">
          La plataforma guarda solo su huella. Si se pierde, se revoca y se crea otra.
        </Banner>
        <div className="flex items-start gap-2 rounded-md border border-gris-300 bg-gris-50 p-3">
          <span className="mono min-w-0 flex-1 break-all whitespace-normal text-gris-900 select-all">{clave.clave}</span>
          <button
            type="button"
            onClick={() => {
              void copiarAlPortapapeles(clave.clave).then((exito) => {
                if (!exito) return
                setCopiada(true)
                setTimeout(() => setCopiada(false), 1500)
              })
            }}
            aria-label={copiada ? 'Clave copiada' : 'Copiar la clave'}
            className="inline-flex size-6 shrink-0 items-center justify-center rounded-md text-gris-600 hover:bg-gris-100 hover:text-gris-900"
          >
            {copiada ? <Check className="size-4 text-verde" aria-hidden="true" /> : <Copy className="size-4" aria-hidden="true" />}
          </button>
        </div>
        <p className="text-cuerpo text-gris-700">
          Va en la cabecera <code className="mono">Authorization: Bearer …</code> del asistente, nunca en una dirección. Guárdela como una contraseña:
          quien la tenga puede hacer lo que sus permisos dicen hasta que la revoque.
        </p>
      </div>
    </PanelLateral>
  )
}

function Revocar({ clave, onCerrar }: { clave: ClaveIa; onCerrar: () => void }) {
  const revocar = useRevocarClaveIa()

  return (
    <DialogoConMotivo
      abierto
      onCancelar={onCerrar}
      onConfirmar={(motivo) => revocar.mutate({ id: clave.id, motivo }, { onSuccess: onCerrar })}
      titulo="Revocar la clave"
      descripcion={
        <>
          <p>
            El asistente que use <strong className="font-semibold text-gris-900">{clave.nombre}</strong> (<span className="mono">{clave.prefijo}</span>)
            deja de entrar en su siguiente petición. Lo que ya hizo se queda, con su autoría.
          </p>
          <p>Una clave revocada no vuelve: para seguir, se crea otra.</p>
        </>
      }
      etiquetaConfirmar="Sí, revocar la clave"
      destructiva
      textoAEscribir={clave.prefijo}
      trabajando={revocar.isPending}
      error={revocar.isError ? { titulo: 'No se pudo revocar la clave.', contexto: mensaje(revocar.error) } : null}
    />
  )
}

const RESULTADOS: ResultadoActividad[] = [
  'correcta',
  'rechazada',
  'fallida',
  'intento',
  'solo_lectura',
  'falta_confirmacion',
  'fuera_de_alcance',
  'tiempo_agotado',
]

function tonoResultado(resultado: ResultadoActividad): Tono {
  switch (resultado) {
    case 'correcta':
      return 'verde'
    case 'intento':
      return 'azul'
    case 'fallida':
    case 'tiempo_agotado':
      return 'rojo'
    default:
      return 'ambar'
  }
}

function Actividad({ clave, onCerrar }: { clave: ClaveIa; onCerrar: () => void }) {
  const [filtro, setFiltro] = useState<FiltroActividadIa>({ pagina: 1 })
  const [exportando, setExportando] = useState(false)
  const [errorExportar, setErrorExportar] = useState<string | null>(null)
  const actividad = useActividadIa(clave.id, filtro)
  const pagina = actividad.data
  const paginas = pagina ? Math.max(1, Math.ceil(pagina.total / pagina.tamano)) : 1
  const cambiar = (cambio: Partial<FiltroActividadIa>) => setFiltro((f) => ({ ...f, ...cambio, pagina: 1 }))
  const th = 'h-[var(--alto-fila)] px-2 text-etiqueta font-semibold text-gris-600'

  return (
    <PanelLateral
      abierto
      onCerrar={onCerrar}
      titulo={`Actividad de ${clave.nombre}`}
      subtitulo={<span className="mono">{clave.prefijo}</span>}
      ancho="ancho"
      pie={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-etiqueta text-gris-600">
            {pagina ? `${pagina.total} filas · página ${pagina.pagina} de ${paginas}` : ' '}
          </span>
          <span className="flex gap-2">
            <Boton tamano="menor" variante="discreto" disabled={(filtro.pagina ?? 1) <= 1} onClick={() => setFiltro((f) => ({ ...f, pagina: (f.pagina ?? 1) - 1 }))}>
              Anterior
            </Boton>
            <Boton tamano="menor" variante="discreto" disabled={(filtro.pagina ?? 1) >= paginas} onClick={() => setFiltro((f) => ({ ...f, pagina: (f.pagina ?? 1) + 1 }))}>
              Siguiente
            </Boton>
            <Boton
              tamano="menor"
              variante="secundario"
              icono={<Download />}
              disabled={exportando}
              onClick={() => {
                setExportando(true)
                setErrorExportar(null)
                exportarActividadIa(clave.id, filtro)
                  .catch((e: unknown) => setErrorExportar(mensaje(e)))
                  .finally(() => setExportando(false))
              }}
            >
              {exportando ? 'Exportando…' : 'Exportar CSV'}
            </Boton>
          </span>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-4">
          <CampoFormulario etiqueta="Desde">
            {(a) => <EntradaTexto {...a} type="date" value={filtro.desde ?? ''} onChange={(e) => cambiar({ desde: e.target.value || undefined })} />}
          </CampoFormulario>
          <CampoFormulario etiqueta="Hasta">
            {(a) => <EntradaTexto {...a} type="date" value={filtro.hasta ?? ''} onChange={(e) => cambiar({ hasta: e.target.value || undefined })} />}
          </CampoFormulario>
          <CampoFormulario etiqueta="Operación o ruta">
            {(a) => <EntradaTexto {...a} value={filtro.operacion ?? ''} placeholder="emitir, /interna/nomina…" onChange={(e) => cambiar({ operacion: e.target.value || undefined })} />}
          </CampoFormulario>
          <CampoFormulario etiqueta="Resultado">
            {(a) => (
              <Selector {...a} value={filtro.resultado ?? ''} onChange={(e) => cambiar({ resultado: e.target.value as ResultadoActividad | '' })}>
                <option value="">Todos</option>
                {RESULTADOS.map((r) => (
                  <option key={r} value={r}>
                    {NOMBRE_RESULTADO[r]}
                  </option>
                ))}
              </Selector>
            )}
          </CampoFormulario>
        </div>

        {errorExportar ? (
          <Banner tono="rojo" titulo="No se pudo exportar.">
            {errorExportar}
          </Banner>
        ) : null}

        {actividad.isError ? (
          <Banner tono="rojo" titulo="No se pudo cargar la actividad.">
            {mensaje(actividad.error)}
          </Banner>
        ) : pagina && pagina.filas.length === 0 ? (
          <EstadoVacio variante="sin-resultados" mensaje="Ninguna petición de esta clave con esos filtros." />
        ) : (
          <div className="overflow-x-auto rounded-md border border-gris-200">
            <table className="w-full min-w-[820px] border-collapse text-cuerpo" data-densidad="compacta">
              <thead className="bg-gris-50">
                <tr className="border-b border-gris-300 text-left">
                  <th scope="col" className={`${th} w-[140px]`}>
                    Cuándo
                  </th>
                  <th scope="col" className={th}>
                    Operación
                  </th>
                  <th scope="col" className={`${th} w-[70px]`}>
                    Vía
                  </th>
                  <th scope="col" className={`${th} w-[60px] text-right`}>
                    Código
                  </th>
                  <th scope="col" className={`${th} w-[190px]`}>
                    Resultado
                  </th>
                </tr>
              </thead>
              <tbody>
                {actividad.isLoading ? (
                  <FilasEsqueleto filas={6} columnas={5} />
                ) : (
                  (pagina?.filas ?? []).map((f) => (
                    <tr key={f.id} className="border-b border-gris-200 last:border-b-0">
                      <td className="numero px-2 py-1 text-left text-gris-700">{formatearFechaHora(f.ocurridaEl)}</td>
                      <td className="max-w-0 px-2 py-1">
                        <span className="block truncate text-gris-900" title={f.operacion ?? f.ruta}>
                          {f.operacion ?? '—'}
                          {f.irreversible ? <span className="ml-1 text-etiqueta text-ambar">· irreversible{f.confirmada ? ', confirmada' : ''}</span> : null}
                        </span>
                        <span className="mono block truncate text-etiqueta text-gris-500" title={f.ruta}>
                          {f.metodo} {f.ruta}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-gris-700">{f.via}</td>
                      <td className="numero px-2 py-1 text-gris-700">{f.estado ?? '—'}</td>
                      <td className="px-2 py-1">
                        <InsigniaEstado tono={tonoResultado(f.resultado)}>{NOMBRE_RESULTADO[f.resultado]}</InsigniaEstado>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PanelLateral>
  )
}

function ComoConectar() {
  const conexion = useConexionIa()
  const [copiado, setCopiado] = useState<string | null>(null)
  const datos = conexion.data

  const copiar = (que: string, texto: string) =>
    void copiarAlPortapapeles(texto).then((exito) => {
      if (!exito) return
      setCopiado(que)
      setTimeout(() => setCopiado(null), 1500)
    })

  const Linea = ({ etiqueta, valor, que }: { etiqueta: string; valor: string; que: string }) => (
    <div className="flex flex-col gap-1">
      <span className="text-etiqueta font-medium text-gris-600">{etiqueta}</span>
      <div className="flex items-center gap-2 rounded-md border border-gris-300 bg-gris-50 px-2 py-1">
        <span className="mono min-w-0 flex-1 break-all whitespace-normal text-gris-900">{valor}</span>
        <button
          type="button"
          onClick={() => copiar(que, valor)}
          aria-label={`Copiar ${etiqueta.toLowerCase()}`}
          className="inline-flex size-6 shrink-0 items-center justify-center rounded-md text-gris-600 hover:bg-gris-100 hover:text-gris-900"
        >
          {copiado === que ? <Check className="size-4 text-verde" aria-hidden="true" /> : <Copy className="size-4" aria-hidden="true" />}
        </button>
      </div>
    </div>
  )

  return (
    <Tarjeta titulo="Cómo conectar">
      <div className="flex flex-col gap-4">
        <p className="flex items-start gap-2 text-cuerpo text-gris-700">
          <Plug className="mt-0.5 size-4 shrink-0 text-gris-500" aria-hidden="true" />
          <span>
            Cualquier cliente compatible con el Model Context Protocol por HTTP se conecta a la dirección MCP con la clave en la cabecera. Quien no use
            MCP tiene lo mismo por HTTP: el catálogo de operaciones y la ejecución.
          </span>
        </p>

        {conexion.isError ? (
          <Banner tono="rojo" titulo="No se pudo cargar la conexión.">
            {mensaje(conexion.error)}
          </Banner>
        ) : datos ? (
          <>
            <div className="grid gap-3 md:grid-cols-2">
              <Linea etiqueta="Dirección MCP" valor={datos.mcp} que="mcp" />
              <Linea etiqueta="Cabecera" valor={datos.cabecera} que="cabecera" />
              <Linea etiqueta="Catálogo por HTTP (GET)" valor={datos.operaciones} que="operaciones" />
              <Linea etiqueta="Ejecución por HTTP (POST)" valor={datos.ejecutar} que="ejecutar" />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-etiqueta font-medium text-gris-600">Configuración de ejemplo para un cliente MCP</span>
              <pre className="mono overflow-x-auto rounded-md border border-gris-300 bg-gris-50 p-3 text-etiqueta text-gris-900">
                {JSON.stringify(datos.ejemplo, null, 2)}
              </pre>
            </div>
            <ul className="flex list-disc flex-col gap-1 pl-5 text-etiqueta text-gris-600">
              <li>
                Especificación MCP {datos.versionMcp}; también atiende a los clientes que saludan con <code className="mono">initialize</code> (
                {datos.versionesAnteriores.join(', ')}).
              </li>
              <li>
                Herramientas: <code className="mono">listar_operaciones</code>, <code className="mono">describir_operacion</code> y{' '}
                <code className="mono">ejecutar_operacion</code>. Lo irreversible pide <code className="mono">confirmar: true</code> según el modo.
              </li>
              <li>La clave nunca va en la dirección: una que llegue en la URL se rechaza, y hay que darla por expuesta.</li>
            </ul>
          </>
        ) : (
          <p className="text-etiqueta text-gris-500">Cargando…</p>
        )}
      </div>
    </Tarjeta>
  )
}
